import { defineStore } from 'pinia'
import type { SSEDrop } from '@/api/models/SSEDrop'
import { i18n } from '@/i18n'
import {
  getArenaRoom,
  joinArenaRoom,
  readyArenaRoom,
} from '@/services/api/requests/arena'
import {
  extractArenaParticipant,
  extractArenaRoom,
  getArenaUiPhase,
  type ArenaRoom,
  type ArenaUiPhase,
} from '@/types/arena'

interface ArenaRoomState {
  room: ArenaRoom | null
  rollsByRound: Record<number, SSEDrop[]>
  wonItemsByParticipant: Record<string, Array<Record<string, unknown>>>
  latestRoundEvent: {
    round: number
    caseId?: string
    rolls: SSEDrop[]
    stamp: number
  } | null
  loading: boolean
  actionLoading: boolean
  error: string | null
  connectionStatus: 'CONNECTED' | 'DISCONNECTED'
  uiPhase: ArenaUiPhase
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error && 'message' in error && typeof error.message === 'string') {
    return error.message
  }
  return fallback
}

export const useArenaRoomStore = defineStore('arenaRoom', {
  state: (): ArenaRoomState => ({
    room: null,
    rollsByRound: {},
    wonItemsByParticipant: {},
    latestRoundEvent: null,
    loading: false,
    actionLoading: false,
    error: null,
    connectionStatus: 'DISCONNECTED',
    uiPhase: 'LOADING',
  }),

  getters: {
    hasRoom: (state) => Boolean(state.room?.id),
    currentRoundRolls: (state) => {
      if (!state.room?.currentRound) return []
      return state.rollsByRound[state.room.currentRound] ?? []
    },
  },

  actions: {
    isCurrentRoomPayload(rawRoom: unknown) {
      if (!this.room?.id) return false

      const next = extractArenaRoom(rawRoom)
      if (!next.id) return false

      if (next.id === this.room.id) return true
      return Boolean(next.shortId && this.room.shortId && next.shortId === this.room.shortId)
    },

    setConnectionStatus(status: 'CONNECTED' | 'DISCONNECTED') {
      this.connectionStatus = status
    },

    setRoom(rawRoom: unknown) {
      const next = extractArenaRoom(rawRoom)
      if (!next.id) return

      const mergedParticipants =
        next.participants ?? this.room?.participants ?? []

      this.room = {
        ...(this.room ?? {}),
        ...next,
        participants: mergedParticipants,
      }
      this.uiPhase = getArenaUiPhase(this.room.status)
    },

    resetRoomState() {
      this.room = null
      this.rollsByRound = {}
      this.wonItemsByParticipant = {}
      this.latestRoundEvent = null
      this.loading = false
      this.actionLoading = false
      this.error = null
      this.connectionStatus = 'DISCONNECTED'
      this.uiPhase = 'LOADING'
    },

    async fetchRoom(shortId: string) {
      this.loading = true
      this.error = null

      try {
        const response = await getArenaRoom(shortId)
        const room = extractArenaRoom(response.room)
        if (!room.id) {
          this.error = i18n.global.t('WHALE_DUEL.store.errors.roomNotFound')
          return
        }

        const participants = response.participants ?? room.participants ?? []
        room.participants = participants.map((participant) => extractArenaParticipant(participant))

        this.room = room
        this.uiPhase = getArenaUiPhase(room.status)

        this.rollsByRound = {}
        ;(response.rolls ?? []).forEach((roll) => {
          const roundNumber = Number((roll as any).round ?? 1)
          if (!this.rollsByRound[roundNumber]) {
            this.rollsByRound[roundNumber] = []
          }
          this.rollsByRound[roundNumber].push(roll)
        })

        this.wonItemsByParticipant = response.wonItems ?? {}
        this.latestRoundEvent = null
      } catch (error) {
        this.error = getErrorMessage(error, i18n.global.t('WHALE_DUEL.store.errors.loadRoom'))
      } finally {
        this.loading = false
      }
    },

    async join(shortId: string, slot: number) {
      this.actionLoading = true
      this.error = null
      try {
        await joinArenaRoom(shortId, { slot })
      } catch (error) {
        this.error = getErrorMessage(error, i18n.global.t('WHALE_DUEL.store.errors.joinRoom'))
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async ready(shortId: string) {
      this.actionLoading = true
      this.error = null
      try {
        await readyArenaRoom(shortId)
      } catch (error) {
        this.error = getErrorMessage(error, i18n.global.t('WHALE_DUEL.store.errors.readyUp'))
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    applyRoomUpdate(rawRoom: unknown) {
      if (!this.isCurrentRoomPayload(rawRoom)) return
      this.setRoom(rawRoom)
    },

    applyParticipantJoined(payload: any) {
      const room = payload?.room ? extractArenaRoom(payload.room) : null
      if (room?.id && this.room?.id === room.id) {
        this.room = {
          ...this.room,
          ...room,
          participants: room.participants ?? this.room.participants,
        }
      }

      if (!this.room || room?.id !== this.room.id) return

      if (payload?.participant && !Array.isArray(room?.participants)) {
        const participant = extractArenaParticipant(payload.participant)
        const participants = this.room.participants ?? []
        if (!participants.some((entry) => entry.id === participant.id)) {
          this.room.participants = [...participants, participant]
        }
      }
    },

    applyParticipantReady(roomId: string, participantId: string) {
      if (!this.room || this.room.id !== roomId) return

      const participants = this.room.participants ?? []
      this.room.participants = participants.map((participant) =>
        participant.id === participantId ? { ...participant, isReady: true } : participant,
      )
    },

    applyArenaStart(roomId: string) {
      if (!this.room || this.room.id !== roomId) return
      this.room.status = 'PLAYING'
      this.uiPhase = 'PLAYING'
    },

    applyArenaRound(payload: any) {
      if (!this.room) return
      const payloadRoomId = payload?.round?.roomId
      if (!payloadRoomId || payloadRoomId !== this.room.id) return

      const roundNumber = Number(payload?.round?.round ?? 1)
      const nextRolls = Array.isArray(payload?.rolls) ? payload.rolls : []
      const previous = this.rollsByRound[roundNumber] ?? []
      this.rollsByRound[roundNumber] = [...previous, ...nextRolls]
      if (nextRolls.length > 0) {
        this.latestRoundEvent = {
          round: roundNumber,
          caseId: payload?.round?.caseId,
          rolls: nextRolls,
          stamp: Date.now(),
        }
      }

      if (!this.room.currentRound || roundNumber > this.room.currentRound) {
        this.room.currentRound = roundNumber
      }
      this.room.status = 'PLAYING'
      this.uiPhase = 'PLAYING'
    },

    applyArenaFinished(payload: any) {
      if (!this.room || payload?.roomId !== this.room.id) return

      this.room.status = 'COMPLETED'
      this.uiPhase = 'FINISHED'

      if (Array.isArray(payload?.participants)) {
        this.room.participants = payload.participants.map((participant) =>
          extractArenaParticipant(participant),
        )
      }

      if (this.room.participants?.length) {
        const next: Record<string, Array<Record<string, unknown>>> = {
          ...this.wonItemsByParticipant,
        }
        const participants = this.room.participants ?? []
        const winnerKeys = Array.isArray(payload?.winners) ? payload.winners.map((it: unknown) => String(it)) : []
        const winnerParticipants = participants.filter(
          (entry) =>
            winnerKeys.includes(String(entry.id ?? '')) || winnerKeys.includes(String(entry.userId ?? '')),
        )

        const wonItems = Array.isArray(payload?.wonItems) ? payload.wonItems : []
        wonItems.forEach((item: Record<string, unknown>) => {
          const userId = String(item.userId ?? '')
          const participantId = String(item.participantId ?? item.arenaParticipantId ?? '')

          let participant =
            participants.find((entry) => String(entry.userId ?? '') === userId) ??
            participants.find((entry) => String(entry.id ?? '') === userId) ??
            participants.find((entry) => String(entry.id ?? '') === participantId) ??
            participants.find((entry) => String(entry.userId ?? '') === participantId)

          // Some bot winner items come without user/participant id. In that case, if only one winner exists,
          // attribute the item to that winner.
          if (!participant && winnerParticipants.length === 1) {
            participant = winnerParticipants[0]
          }

          if (!participant?.id) return
          if (!next[participant.id]) {
            next[participant.id] = []
          }
          next[participant.id].push(item)
        })

        // Fallback: when finished payload doesn't include resolvable wonItems, infer winner item count
        // from all round rolls so final result cards don't incorrectly show 0 for the winner.
        if (wonItems.length === 0 && winnerParticipants.length === 1) {
          const winner = winnerParticipants[0]
          const totalRolls = Object.values(this.rollsByRound).reduce<number>((sum, rolls) => {
            const count = Array.isArray(rolls) ? rolls.length : 0
            return sum + count
          }, 0)

          if (winner?.id && totalRolls > 0) {
            next[winner.id] = Array.from({ length: Number(totalRolls) }, (_, index) => ({
              id: `inferred-win-${winner.id}-${index}`,
              inferred: true,
            }))
          }
        }

        // Business rule: losers should always show at least 1 won item in final result.
        participants.forEach((participant) => {
          const participantId = String(participant.id ?? '')
          if (!participantId) return

          const isWinner =
            Boolean(participant.winner || participant.hasWon) ||
            winnerKeys.includes(participantId) ||
            winnerKeys.includes(String(participant.userId ?? ''))

          if (isWinner) return

          const currentCount = next[participantId]?.length ?? 0
          if (currentCount > 0) return

          next[participantId] = [
            {
              id: `inferred-loss-${participantId}`,
              inferred: true,
            },
          ]
        })

        this.wonItemsByParticipant = next
      }
    },
  },
})
